/* SAP cart-drawer plan-change controller.
 *
 * Wires the cart-drawer controls that change a line between one-time and
 * subscription (and between cadences):
 *   - .sap-cd-upgrade-cta  — "Upgrade to Subscription" button on one-time lines
 *   - .sap-cd-plan-select  — cadence <select> on subscription lines (also offers
 *                            "One-time purchase" to downgrade)
 *
 * Before this controller these controls had markup + CSS + a transition pill but
 * NO JavaScript at all — clicking did nothing (no listener, no /cart/change.js).
 *
 * Re-render safety: the drawer re-renders its line items via Aurora's
 * 'update-nodes' mechanism on every cart change, replacing these elements. So we
 * use EVENT DELEGATION on document — the listeners survive every re-render.
 *
 * Refresh path (identical to assets/sap-pdp-cadence-box.js): request the
 * cart-drawer section back with the change via the Section Rendering API
 * (sections + sections_url on /cart/change.js) and hand the rendered section to
 * the drawer's own purchaseHandler — it emits 'update-nodes' so every cart
 * sub-block re-renders and the line swaps to its new state. The freshly-rendered
 * Liquid replaces the transition pill, so the "Upgrading…" state ends naturally.
 *
 * Line identifier: /cart/change.js needs a 1-based `line`. The controls carry a
 * 0-based index (data-line-index / data-line), so line = index + 1. Using the
 * line position (not the variant id) disambiguates two lines of the same product
 * — e.g. a one-time AND a subscription "Day" in the same cart.
 */
(function () {
  "use strict";

  /* Cart-wide in-flight lock: /cart/change.js line numbers are positional, so a
     second change fired while one is pending can land on a renumbered line after
     Shopify merges/removes lines — mutating the WRONG line. One change at a time. */
  var inFlight = false;

  function drawerAndSection(control) {
    var root = control.closest("cart-drawer, cart-page");
    var sectionId = (root && root.dataset.sectionId) || "cart-drawer";
    return { root: root, sectionId: sectionId };
  }

  function setTransition(planRow, label) {
    if (!planRow) return;
    var labelEl = planRow.querySelector(".sap-cd-plan-transition__label");
    if (labelEl) labelEl.textContent = label;
    planRow.classList.add("is-transitioning");
  }

  function clearTransition(planRow) {
    if (planRow) planRow.classList.remove("is-transitioning");
  }

  /* POST a line change to /cart/change.js, then refresh the drawer natively.
     opts: { line, quantity, sellingPlan (string|''), planRow, label,
             control (the button/select — recovered on error/no-render),
             revertValue (select value to restore on failure) } */
  function changeLine(opts) {
    var ds = drawerAndSection(opts.control);
    var applied = false;
    var reloading = false;
    var wasInert = ds.root && ds.root.inert;
    function reloadCart() {
      reloading = true;
      if (ds.root) ds.root.inert = true;
      var cart = new URL((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || "/") + "cart", window.location.origin);
      var current = new URL(window.location.href);
      if (current.pathname === cart.pathname) cart.search = current.search;
      else if (current.searchParams.has("preview_theme_id")) cart.searchParams.set("preview_theme_id", current.searchParams.get("preview_theme_id"));
      window.location.assign(cart.href);
    }
    inFlight = true;
    var errorMessage = opts.planRow.querySelector("[data-bag-plan-error-message]");
    if (errorMessage) {
      errorMessage.hidden = true;
      errorMessage.textContent = "";
    }
    var liveStatus = document.getElementById("SapBagStatus");
    if (liveStatus) liveStatus.textContent = "";
    setTransition(opts.planRow, opts.label);

    var body = new FormData();
    body.append("line", opts.line);
    if (opts.quantity) body.append("quantity", opts.quantity);
    /* selling_plan: a plan id changes/upgrades the line; '' removes it (revert to
       one-time). Always sent so the change is explicit. */
    body.append("selling_plan", opts.sellingPlan == null ? "" : opts.sellingPlan);
    body.append("sections", ds.sectionId);
    body.append("sections_url", window.location.pathname);

    return fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: body,
    })
      .then(function (res) {
        applied = res.ok;
        return res.text().then(function (text) {
          var json = null;
          try {
            json = JSON.parse(text);
          } catch (_) {}
          if (!res.ok) {
            throw new Error((json && (json.description || json.message)) || text.slice(0, 200) || "HTTP " + res.status);
          }
          return json;
        });
      })
      .then(async function (json) {
        var sectionHTML = json && json.sections && json.sections[ds.sectionId];
        if (!sectionHTML) {
          if (ds.root) ds.root.inert = true;
          var address = new URL(window.location.href);
          address.hash = "";
          address.searchParams.delete("section_id");
          address.searchParams.set("sections", ds.sectionId);
          var response = await fetch(address.href, { credentials: "same-origin", cache: "no-store" });
          if (!response.ok) throw new Error("Cart section unavailable");
          var sections = await response.json();
          sectionHTML = sections && sections[ds.sectionId];
          json = Object.assign({}, json, { sections: sections });
        }
        if (ds.root && typeof ds.root.purchaseHandler === "function" && sectionHTML) {
          /* purchaseHandler expects a PARSED Document (Aurora calls parseHTML
             first). It emits 'update-nodes' so the items re-render with the new
             line state, then opens the drawer (idempotent — already open here). */
          var parsed = new DOMParser().parseFromString(sectionHTML, "text/html");
          if (!parsed.querySelector(ds.root.matches("cart-page") ? "cart-page" : '[data-sidebar-template="' + ds.root.id + '"]')) throw new Error("Cart section unavailable");
          ds.root.inert = wasInert;
          await ds.root.purchaseHandler(parsed, json);
          ds.root.setLoadingStatus(false);
          if (ds.root.matches("cart-page")) ds.root.setCartState();
        } else {
          reloadCart();
          return;
        }
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      })
      .catch(function (err) {
        if (applied) {
          reloadCart();
          return;
        }
        console.error("[sap-cart-plan-change]", err);
        /* Recover the control — otherwise the button stays permanently disabled
           and the select keeps displaying a cadence the server never applied. */
        if (opts.control) {
          opts.control.disabled = false;
          if (opts.revertValue != null && opts.control.tagName === "SELECT") {
            opts.control.value = opts.revertValue;
          }
        }
        clearTransition(opts.planRow);
        if (ds.root) ds.root.setLoadingStatus(false);
        if (liveStatus) liveStatus.textContent = "";
        if (errorMessage) {
          errorMessage.hidden = false;
          errorMessage.textContent = opts.planRow.dataset.bagPlanError;
        }
        if (opts.control) opts.control.focus({ preventScroll: true });
      })
      .finally(function () {
        if (!reloading) inFlight = false;
      });
  }

  /* ── Upgrade button (one-time → subscription) ── */
  document.addEventListener("click", function (e) {
    var btn = e.target.closest(".sap-cd-upgrade-cta");
    if (!btn || btn.disabled || inFlight) return;
    e.preventDefault();
    btn.disabled = true;
    changeLine({
      line: parseInt(btn.dataset.lineIndex, 10) + 1,
      quantity: btn.dataset.quantity,
      sellingPlan: btn.dataset.sellingPlanId,
      planRow: btn.closest(".cart-item__plan-row"),
      label: "Upgrading…",
      control: btn,
    });
  });

  /* ── Cadence select (change cadence, or "One-time purchase" → downgrade) ── */
  document.addEventListener("change", function (e) {
    var sel = e.target.closest(".sap-cd-plan-select");
    if (!sel) return;
    if (inFlight) {
      /* Another change is pending — accepting this one would race it with a
         stale line index. Snap back to the last server-confirmed cadence. */
      sel.value = sel.dataset.lastValue || "";
      return;
    }
    sel.disabled = true;
    changeLine({
      line: parseInt(sel.dataset.line, 10) + 1,
      quantity: sel.dataset.quantity,
      sellingPlan: sel.value, // '' = one-time, else a selling_plan id
      planRow: sel.closest(".cart-item__plan-row"),
      label: "Updating…",
      control: sel,
      revertValue: sel.dataset.lastValue || "",
    });
  });
})();
